<template lang="pug">
//- Modal da imagem de divulgação de cupom / promoção / boas-vindas da loja.
//- Abre manualmente (botão "Imagem"), logo após a ativação (recemAtivado) ou
//- pelo card de boas-vindas (onboarding e aba Marketing).
//- Sem `scrollable`: quem rola é o próprio .v-dialog (o card inteiro), e as
//- ações ficam presas ao pé do modal quando o conteúdo não cabe na tela.
v-dialog(v-model="aberto", max-width="460")
  v-card.Poppins.arteDivulgacao(style="border-radius:16px;")

    //- ─── Cabeçalho ───────────────────────────────────────────────────────────
    .bd-header
      .bd-header__eyebrow(v-if="selo")
        v-icon(style="color:#12603A;font-size:15px;") check_circle
        span {{ selo }}
      .bd-header__title {{ cabecalho }}
      .bd-header__subtitle {{ titulo }}

    //- ─── Prévia ──────────────────────────────────────────────────────────────
    .bd-preview
      .bd-preview__frame(:class="classeMoldura")
        img.bd-preview__img(v-if="imagem && !gerando", :src="imagem", alt="Prévia da imagem")

        .bd-preview__state(v-if="gerando")
          v-progress-circular(:width="3", :size="34", color="#5BCE8C", indeterminate)
          span Gerando arte...

        .bd-preview__state.bd-preview__state--erro(v-if="erro && !gerando")
          v-icon(style="color:#F0A9A5;font-size:28px;") error_outline
          span {{ erro }}
          button.bd-btn.bd-btn--ghost(type="button", @click="tentarNovamente") Tentar novamente

    //- ─── Aviso (ex.: loja ainda não ativada) ─────────────────────────────────
    .bd-aviso(v-if="aviso")
      v-icon(style="color:#B26A00;font-size:16px;") schedule
      span {{ aviso }}

    //- ─── Opções de arte (só quando há mais de uma) ───────────────────────────
    .bd-paleta(v-if="artes.length > 1")
      span.bd-paleta__label Arte
      .bd-paleta__chips
        button.bd-chip(
          v-for="arte in artes",
          :key="arte.key",
          type="button",
          :class="{ 'bd-chip--active': layout === arte.key }",
          :disabled="gerando",
          @click="selecionarArte(arte.key)"
        ) {{ arte.nome }}

    //- ─── Formato (boas-vindas: feed ou stories) ──────────────────────────────
    .bd-paleta(v-if="formatos.length")
      span.bd-paleta__label Formato
      .bd-paleta__chips
        button.bd-chip(
          v-for="item in formatos",
          :key="item.key",
          type="button",
          :class="{ 'bd-chip--active': formato === item.key }",
          :disabled="gerando",
          @click="selecionarFormato(item.key)"
        ) {{ item.nome }}

    //- ─── Ações ───────────────────────────────────────────────────────────────
    .bd-acoes
      .bd-actions
        button.bd-btn.bd-btn--primary(type="button", :disabled="!imagem || gerando", @click="compartilhar")
          v-icon(style="color:#fff;font-size:16px;") share
          span Compartilhar
        button.bd-btn.bd-btn--secondary(type="button", :disabled="!imagem || gerando", @click="baixar")
          v-icon(style="color:#fff;font-size:16px;") file_download
          span Baixar

      .bd-actions.bd-actions--sec
        button.bd-btn.bd-btn--ghost(type="button", :disabled="!imagem || gerando || copiando", @click="copiarImagem")
          v-icon(style="color:#4a4a4a;font-size:15px;") content_copy
          span Copiar imagem
        button.bd-btn.bd-btn--ghost(type="button", @click="copiarLegenda")
          v-icon.fa.fa-whatsapp(style="color:#4a4a4a;font-size:15px;")
          span Copiar legenda

    .bd-hint {{ dica }}

    .bd-footer
      button.bd-btn.bd-btn--close(type="button", @click="fechar") Fechar
</template>

<script>
import ArteDivulgacao from '@/util/arteDivulgacao';

export default {
  name: 'DialogArteDivulgacao',
  data() {
    return {
      aberto: false,
      tipo: 'cupom',
      entidade: {},
      estabelecimento: {},
      recemAtivado: false,
      origem: 'lista',
      aviso: null,
      layout: null,
      formato: ArteDivulgacao.FORMATOS_BOAS_VINDAS[0].key,
      imagem: null,
      gerando: false,
      copiando: false,
      erro: null,
      // Cada arte (e formato) já gerada fica em memória enquanto o modal está
      // aberto — alternar entre elas não refaz a requisição.
      cache: {},
    };
  },
  computed: {
    isCupom() {
      return this.tipo === 'cupom';
    },
    isBoasVindas() {
      return this.tipo === 'boas-vindas';
    },
    artes() {
      if (this.isBoasVindas) {
        return ArteDivulgacao.artesBoasVindas(this.estabelecimento);
      }
      return this.isCupom ? ArteDivulgacao.ARTES_CUPOM : ArteDivulgacao.ARTES_PROMOCAO;
    },
    // Só a de boas-vindas sai em mais de um formato; cupom e promoção são 1080x1080.
    formatos() {
      return this.isBoasVindas ? ArteDivulgacao.FORMATOS_BOAS_VINDAS : [];
    },
    formatoAtual() {
      return this.formatos.find(item => item.key === this.formato) || null;
    },
    classeMoldura() {
      if (!this.formatoAtual) {
        return null;
      }
      return `bd-preview__frame--${this.formatoAtual.sufixo}`;
    },
    dica() {
      if (this.formatoAtual) {
        return this.formatoAtual.dica;
      }
      return 'Imagem quadrada de 1080×1080, pronta para stories, feed e WhatsApp.';
    },
    selo() {
      if (this.isBoasVindas) {
        return this.origem === 'onboarding' ? 'Onboarding concluído' : null;
      }
      if (!this.recemAtivado) {
        return null;
      }
      return this.isCupom ? 'Cupom ativado' : 'Promoção ativada';
    },
    cabecalho() {
      if (this.isBoasVindas) {
        return 'Divulgue a chegada da loja';
      }
      return this.recemAtivado ? 'Divulgue agora' : 'Imagem de divulgação';
    },
    titulo() {
      if (this.isBoasVindas) {
        return this.estabelecimento.nome;
      }
      if (this.isCupom) {
        return `Cupom ${String(this.entidade.codigo || '').toUpperCase()}`;
      }
      return this.entidade.nome;
    },
    // Página da loja no site do Bigou ou no cardápio digital — vai na legenda
    // de boas-vindas. Cadastro incompleto gera URL quebrada: aí fica sem link.
    linkLoja() {
      try {
        const url = this.$root.getURL(this.estabelecimento);
        return url && url.indexOf('undefined') === -1 ? url : null;
      } catch (err) {
        return null;
      }
    },
    legenda() {
      if (this.isBoasVindas) {
        return ArteDivulgacao.legendaBoasVindas(this.estabelecimento, this.linkLoja);
      }
      return this.isCupom
        ? ArteDivulgacao.legendaCupom(this.entidade, this.estabelecimento)
        : ArteDivulgacao.legendaPromocao(this.entidade, this.estabelecimento);
    },
    filename() {
      const loja = this.estabelecimento.nome || '';
      if (this.isBoasVindas) {
        // O formato vem antes da loja: nome comprido não corta o sufixo.
        return ArteDivulgacao.nomeArquivo(`boas-vindas-${this.formatoAtual.sufixo}`, loja);
      }
      return this.isCupom
        ? ArteDivulgacao.nomeArquivo('cupom', `${loja}-${this.entidade.codigo}`)
        : ArteDivulgacao.nomeArquivo('promocao', `${loja}-${this.entidade.nome}`);
    },
  },
  methods: {
    _nomeEvento(evento) {
      if (this.isBoasVindas) {
        return evento.replace(/^promo_/, 'welcome_');
      }
      return this.isCupom ? evento.replace(/^promo_/, 'cupom_') : evento;
    },
    _identificadores() {
      if (this.isBoasVindas) {
        return { formato: this.formato };
      }
      if (this.isCupom) {
        return {
          cupom_id: this.entidade.id || null,
          cupom_codigo: String(this.entidade.codigo || '').toUpperCase(),
        };
      }
      return {
        promocao_id: this.entidade.id || null,
        campanha_id: this.entidade.campanha_promocao_id || null,
        promocao_nome: this.entidade.nome || null,
      };
    },
    _loja() {
      const loja = this.estabelecimento || {};
      return {
        loja_id: String(loja.id || ''),
        localidade_id: String(loja.localidade_id || ''),
        localidade: String(loja.localidade || ''),
      };
    },
    _logArte(evento, extras) {
      if (!this.$firebase || !this.$firebase.logEvent) {
        return;
      }
      this.$firebase.logEvent(this._nomeEvento(evento), {
        tipo: this.tipo,
        layout: this.layout,
        origem: this.origem,
        ...this._loja(),
        ...this._identificadores(),
        ...(extras || {}),
      });
    },
    // params: { cupom | promo, estabelecimento, recemAtivado, origem }
    abrirCupom(params) {
      this._abrir('cupom', params.cupom, params);
    },
    // Guarda final: nenhuma tela abre a arte de uma promoção segmentada.
    abrirPromocao(params) {
      if (ArteDivulgacao.promocaoSegmentada(params.promo)) {
        window.getApp.$emit('APP_ALERT', { color: 'red', text: ArteDivulgacao.MOTIVO_SEGMENTADA });
        return;
      }
      this._abrir('promocao', params.promo, params);
    },
    // params: { estabelecimento, origem ('onboarding' | 'marketing'), aviso }
    abrirBoasVindas(params) {
      this._abrir('boas-vindas', null, params);
    },
    _abrir(tipo, entidade, params) {
      this.tipo = tipo;
      this.entidade = entidade || {};
      this.estabelecimento = params.estabelecimento || {};
      this.recemAtivado = !!params.recemAtivado;
      this.origem = params.origem || (params.recemAtivado ? 'ativacao' : 'lista');
      this.aviso = params.aviso || null;
      this.layout = this.artes[0].key;
      this.formato = ArteDivulgacao.FORMATOS_BOAS_VINDAS[0].key;
      this.imagem = null;
      this.erro = null;
      this.gerando = false;
      this.copiando = false;
      this.cache = {};
      this.aberto = true;
      this._logArte('promo_art_opened');
      this.gerar();
    },
    montarPayload(layout, formato) {
      if (this.isBoasVindas) {
        return ArteDivulgacao.payloadBoasVindas(this.estabelecimento, layout, formato);
      }
      return this.isCupom
        ? ArteDivulgacao.payloadCupom(this.entidade, this.estabelecimento, layout)
        : ArteDivulgacao.payloadPromocao(this.entidade, this.estabelecimento, layout);
    },
    _chave(layout, formato) {
      return this.isBoasVindas ? `${layout}|${formato}` : layout;
    },
    // A loja selecionada não traz `categorias` (o painel carrega só delivery e
    // vendas): busca uma vez para a categoria principal sair sob o nome.
    async carregarCategorias() {
      const loja = this.estabelecimento;
      if (!loja.id || loja.categorias) {
        return;
      }
      let categorias = [];
      try {
        const { data } = await this.$axios.get('/estabelecimento', {
          params: { id: loja.id, fields: 'categorias' },
        });
        const encontrada = (data || []).find(e => e.id === loja.id);
        categorias = (encontrada && encontrada.categorias) || [];
      } catch (err) {
        // Sem categoria a arte sai só com o nome.
      }
      // O modal pode ter sido reaberto para outra loja enquanto buscava.
      if (this.estabelecimento === loja) {
        this.estabelecimento = { ...loja, categorias };
      }
    },
    async gerar() {
      const layout = this.layout;
      const formato = this.formato;
      const chave = this._chave(layout, formato);

      if (this.cache[chave]) {
        this.imagem = this.cache[chave];
        return;
      }

      this.gerando = true;
      this.erro = null;

      try {
        if (this.isBoasVindas) {
          await this.carregarCategorias();
        }
        const dataUrl = await ArteDivulgacao.gerar(this.$axios, this.montarPayload(layout, formato));
        this.$set(this.cache, chave, dataUrl);
        this._logArte('promo_art_generated', { layout });
        // Trocar de arte durante a geração não pode sobrescrever a escolha atual.
        if (this._chave(this.layout, this.formato) === chave) {
          this.imagem = dataUrl;
        }
      } catch (err) {
        this.erro = (err && err.message) || 'Não foi possível gerar a imagem';
        this._logArte('promo_art_error', { layout, etapa: 'fragmento' });
      } finally {
        this.gerando = false;
      }
    },
    selecionarArte(layout) {
      if (this.layout === layout) {
        return;
      }
      this.layout = layout;
      this._logArte('promo_art_layout_changed');
      this.gerar();
    },
    selecionarFormato(formato) {
      if (this.formato === formato) {
        return;
      }
      this.formato = formato;
      this._logArte('promo_art_format_changed');
      this.gerar();
    },
    async compartilhar() {
      if (!this.imagem) {
        return;
      }
      try {
        const resultado = await ArteDivulgacao.compartilhar({
          dataUrl: this.imagem,
          filename: this.filename,
          title: this.titulo,
          text: this.legenda,
        });
        if (resultado === 'download') {
          // Navegador sem Web Share: o arquivo cai como download.
          this._logArte('promo_art_saved', { via: 'compartilhar' });
          window.getApp.$emit('APP_ALERT', { color: 'success', text: 'Imagem baixada! Anexe no WhatsApp ou nas redes da loja.' });
          return;
        }
        if (resultado === 'share') {
          this._logArte('promo_art_shared');
        }
      } catch (err) {
        this._logArte('promo_art_error', { etapa: 'compartilhar' });
        window.getApp.$emit('APP_ALERT', { color: 'red', text: 'Não foi possível compartilhar a imagem' });
      }
    },
    baixar() {
      if (!this.imagem) {
        return;
      }
      ArteDivulgacao.baixar(this.imagem, this.filename);
      this._logArte('promo_art_saved', { via: 'baixar' });
      window.getApp.$emit('APP_ALERT', { color: 'success', text: 'Imagem baixada!' });
    },
    // O guard evita que um duplo clique dispare duas escritas no clipboard.
    async copiarImagem() {
      if (!this.imagem || this.copiando) {
        return;
      }
      this.copiando = true;
      try {
        await ArteDivulgacao.copiar(this.imagem);
        this._logArte('promo_art_image_copied');
        window.getApp.$emit('APP_ALERT', { color: 'success', text: 'Imagem copiada para a área de transferência!' });
      } catch (err) {
        this._logArte('promo_art_error', { etapa: 'copiar_imagem' });
        window.getApp.$emit('APP_ALERT', { color: 'red', text: (err && err.message) || 'Não foi possível copiar a imagem' });
      } finally {
        this.copiando = false;
      }
    },
    async copiarLegenda() {
      try {
        await ArteDivulgacao.copiarTexto(this.legenda);
        this._logArte('promo_art_caption_copied');
        window.getApp.$emit('APP_ALERT', { color: 'success', text: 'Legenda copiada!' });
      } catch (err) {
        this._logArte('promo_art_error', { etapa: 'copiar_legenda' });
        window.getApp.$emit('APP_ALERT', { color: 'red', text: (err && err.message) || 'Não foi possível copiar a legenda' });
      }
    },
    tentarNovamente() {
      this.$delete(this.cache, this._chave(this.layout, this.formato));
      this.gerar();
    },
    fechar() {
      this.aberto = false;
    },
  },
};
</script>

<style lang="scss">
// ─── Arte de divulgação — modal de imagem de cupom / promoção ────────────────
.arteDivulgacao {
  padding: 22px 24px 18px;
}

// ─── Cabeçalho ──────────────────────────────────────────────────────────────
.arteDivulgacao .bd-header {
  margin-bottom: 16px;
}
.arteDivulgacao .bd-header__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #e7f7ee;
  color: #12603a;
  border-radius: 20px;
  padding: 3px 11px;
  font-size: 11px;
  font-weight: 700;
  margin-bottom: 8px;
}
.arteDivulgacao .bd-header__title {
  font-size: 19px;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.25;
}
.arteDivulgacao .bd-header__subtitle {
  font-size: 13px;
  color: #8a8a8a;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// ─── Prévia ─────────────────────────────────────────────────────────────────
.arteDivulgacao .bd-preview {
  display: flex;
  align-items: flex-start;
  justify-content: center;
}
.arteDivulgacao .bd-preview__frame {
  width: 288px;
  max-width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 18px;
  background: #12140f;
  background-image: linear-gradient(135deg, #14171a 0%, #0c0e10 100%);
  border: 1px solid #e6e6e6;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
// Boas-vindas: a moldura segue a proporção, com a mesma altura nos dois formatos.
.arteDivulgacao .bd-preview__frame--feed {
  width: 272px;
  aspect-ratio: 4 / 5;
}
.arteDivulgacao .bd-preview__frame--stories {
  width: 191px;
  aspect-ratio: 9 / 16;
}
.arteDivulgacao .bd-preview__img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}
.arteDivulgacao .bd-preview__state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: rgba(255, 255, 255, 0.62);
  font-size: 12.5px;
  text-align: center;
  padding: 0 26px;
}
.arteDivulgacao .bd-preview__state--erro {
  color: #f0a9a5;
}

// ─── Aviso ──────────────────────────────────────────────────────────────────
.arteDivulgacao .bd-aviso {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 16px 0 0;
  padding: 10px 12px;
  border-radius: 10px;
  background: #fff4e5;
  color: #8a4b00;
  font-size: 12.5px;
  line-height: 1.45;
}

// ─── Paleta ─────────────────────────────────────────────────────────────────
.arteDivulgacao .bd-paleta {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px;
  margin: 16px 0 4px;
}
.arteDivulgacao .bd-paleta + .bd-paleta {
  margin-top: 8px;
}
.arteDivulgacao .bd-paleta__label {
  flex: 0 0 auto;
  white-space: nowrap;
  font-size: 11px;
  font-weight: 700;
  color: #a0a0a0;
  text-transform: uppercase;
  letter-spacing: 1.4px;
}
.arteDivulgacao .bd-paleta__chips {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
}
.arteDivulgacao .bd-chip {
  flex: 0 0 auto;
  min-height: 30px;
  padding: 0 14px;
  border-radius: 15px;
  border: 2px solid #dcdcdc;
  background: #fff;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  color: #6b6b6b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  outline: none;
  transition: border-color 0.15s ease, color 0.15s ease, transform 0.15s ease;
}
.arteDivulgacao .bd-chip:hover:not([disabled]) {
  transform: scale(1.08);
}
.arteDivulgacao .bd-chip[disabled] {
  opacity: 0.5;
  cursor: default;
}
.arteDivulgacao .bd-chip--active {
  border-color: #1a1a1a;
  color: #1a1a1a;
}

// ─── Ações ──────────────────────────────────────────────────────────────────
.arteDivulgacao .bd-acoes {
  position: sticky;
  bottom: 0;
  z-index: 1;
  margin: 0 -24px;
  padding: 16px 24px 14px;
  background: linear-gradient(rgba(255, 255, 255, 0), #fff 16px);
}
.arteDivulgacao .bd-actions {
  display: flex;
  gap: 10px;
}
.arteDivulgacao .bd-actions--sec {
  margin-top: 8px;
}
.arteDivulgacao .bd-btn {
  flex: 1;
  border: 1px solid transparent;
  border-radius: 10px;
  padding: 11px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  outline: none;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}
.arteDivulgacao .bd-btn[disabled] {
  opacity: 0.45;
  cursor: default;
}
.arteDivulgacao .bd-btn--primary {
  background: #1d9a5a;
  color: #fff;
}
.arteDivulgacao .bd-btn--primary:hover:not([disabled]) {
  background: #17804a;
}
.arteDivulgacao .bd-btn--secondary {
  background: #1a1a1a;
  color: #fff;
}
.arteDivulgacao .bd-btn--secondary:hover:not([disabled]) {
  background: #333;
}
.arteDivulgacao .bd-btn--ghost {
  background: #f4f4f4;
  color: #4a4a4a;
  font-weight: 500;
  font-size: 12.5px;
  padding: 9px 12px;
}
.arteDivulgacao .bd-btn--ghost:hover:not([disabled]) {
  background: #e9e9e9;
}
.arteDivulgacao .bd-btn--close {
  background: transparent;
  color: #999;
  font-weight: 500;
  font-size: 13px;
}
.arteDivulgacao .bd-btn--close:hover {
  color: #555;
}

// ─── Rodapé ─────────────────────────────────────────────────────────────────
.arteDivulgacao .bd-hint {
  text-align: center;
  font-size: 11.5px;
  color: #a8a8a8;
}
.arteDivulgacao .bd-footer {
  margin-top: 6px;
  display: flex;
}
</style>
